import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, CheckIcon, ChevronDownIcon, CircleXIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldIcon, SparklesIcon, TrophyIcon } from 'lucide-react';
import { bonuses, faqs, testimonials, tomos } from '../data/content';
import { Button } from './ui/Button';
type Testimonial = {
  initials: string;
  name: string;
  place: string;
  text: string;
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-bold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-accent'}`} data-id="element-16" data-mp="1iqulbclwus8q">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-extrabold leading-tight text-slate-900 md:text-4xl" data-id="element-17" data-mp="2z2hrld5myj33">
      {children}
    </h2>;
}
function TestimonialCard({
  item
}: {
  item: Testimonial;
}) {
  return <article className="relative flex flex-col rounded-2xl border border-sky-100 bg-white p-6 shadow-sm" data-id="element-18" data-mp="1bovsey79f7az">
      <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-primary/20" data-id="element-19" data-mp="16ohj7ctx9rbd" />
      <div className="mb-4 text-sm text-gold" data-id="element-20" data-mp="12nfkcx3x3h9x">★★★★★</div>
      <p className="mb-5 flex-1 text-base font-medium italic leading-relaxed text-slate-700" data-id="element-21" data-mp="xqfbkefoo0f4">
        “{item.text}”
      </p>
      <div className="mt-auto flex items-center gap-3 border-t border-sky-100 pt-4" data-id="element-22" data-mp="srujx3ehyp2a">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-primary/25 bg-sky-50 font-display text-sm font-bold text-accent" data-id="element-23" data-mp="nzueivfnr3yb">
          {item.initials}
        </span>
        <div data-id="element-24" data-mp="f0pj3xze7qqd">
          <p className="text-sm font-bold text-slate-900" data-id="element-25" data-mp="f0zbfs7wu9z8">{item.name}</p>
          <p className="text-xs text-slate-500" data-id="element-26" data-mp="9z9xa3pe5g6x">{item.place}</p>
        </div>
      </div>
    </article>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-14 md:py-16" data-id="element-27" data-mp="53kulri2rj6e">
      <Eyebrow data-id="element-28" data-mp="3ri7awtpkml34">⭐ Lo que dicen los que ya lo tienen</Eyebrow>
      <SectionTitle data-id="element-29" data-mp="d8osnjdbjur">
        Personas reales que ya dejaron atrás{' '}
        <span className="text-accent" data-id="element-30" data-mp="acrn1fcgz7d">el dolor de espalda</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-31" data-mp="3hmnnkvp2oupz">
        {testimonials.map(item => <TestimonialCard key={item.initials} item={item} data-id="element-32" data-mp="3mk5uyhnfz1vv" />)}
      </div>
    </section>;
}
const pains = ['Buscás ejercicios en internet y no sabés si te hacen bien o te empeoran.', 'Empezás rutinas que abandonás porque no ves progresión.', 'El dolor vuelve siempre al mismo lugar porque nunca trabajaste la causa real.', 'Pasás horas sentado sin saber cómo compensar el daño postural acumulado.', 'Tenés miedo de lesionarte más si hacés ejercicio sin una guía clara.'];
const transformations = ['Abrís el tomo y sabés qué hacer hoy', 'Movilidad, fuerza, postura y estabilidad', 'Rutinas para casa o gimnasio', 'Progresión real semana a semana'];
export function PainAndTransformation() {
  return <section className="border-y border-sky-100 bg-white px-4 py-12 md:py-16" data-id="element-33" data-mp="2hlacz9as6fgi">
      <div className="mx-auto max-w-4xl" data-id="element-34" data-mp="2hj6b0wwfzqiv">
        <Eyebrow danger data-id="element-35" data-mp="2wa6jh1x6vkf0">😤 La realidad</Eyebrow>
        <SectionTitle data-id="element-36" data-mp="2wbzpcezza0ly">
          ¿Cuánto tiempo más vas a convivir con el dolor de espalda{' '}
          <span className="text-accent" data-id="element-37" data-mp="2rcwwru16pmuv">sin hacer nada real?</span>
        </SectionTitle>
        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-38" data-mp="23lhrpti0wrfx">
          {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-sky-100 bg-sky-50/60 p-4" data-id="element-39" data-mp="1ynpoi3bxhiwf">
              <CircleXIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-40" data-mp="28jsytwgxonug" />
              <span className="text-sm font-medium text-slate-700" data-id="element-41" data-mp="3o12wc2j91p2a">{pain}</span>
            </div>)}
          <div className="flex items-center justify-center rounded-xl border border-primary/20 bg-sky-50 p-4 text-center" data-id="element-42" data-mp="3jzre4i2pg644">
            <p className="font-bold text-slate-700" data-id="element-43" data-mp="j51oq9tkjlib">
              No es falta de voluntad.
              <br data-id="element-44" data-mp="n53js04stgom" />
              <span className="text-lg text-accent" data-id="element-45" data-mp="n6mznpmjlppv">Es falta de sistema.</span>
            </p>
          </div>
        </div>

        <div className="mt-12" data-id="element-46" data-mp="s3n6w3v4oyik">
          <Eyebrow data-id="element-47" data-mp="x3rpbzflk3bf">
            <span className="inline-flex items-center gap-2" data-id="element-48" data-mp="x4b7nh95stpz">
              <SparklesIcon className="h-4 w-4" data-id="element-49" data-mp="x1orit87d5w5" /> La transformación
            </span>
          </Eyebrow>
          <SectionTitle data-id="element-50" data-mp="164pvoodicmx6">
            ¿Y si mañana te levantaras sabiendo exactamente{' '}
            <span className="text-accent" data-id="element-51" data-mp="162d5jnxlj691">qué hacer con tu espalda?</span>
          </SectionTitle>
          <div className="mt-7 rounded-3xl border border-primary/20 bg-sky-50/70 p-6 md:p-8" data-id="element-52" data-mp="1axsgo847yo1r">
            <p className="text-center text-base font-bold leading-relaxed text-slate-900 md:text-lg" data-id="element-53" data-mp="1fwdgi6fk75po">
              Abrís el tomo, seguís el protocolo y empezás a moverte con confianza. Sin dudar si el
              ejercicio te sirve o te empeora.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-54" data-mp="3abivga5y0i1y">
              {transformations.map(item => <div key={item} className="rounded-xl border border-primary/20 bg-white p-3 text-center text-xs font-bold text-accent md:text-sm" data-id="element-55" data-mp="35dr5hlouz60e">
                  {item}
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-16" data-id="element-56" data-mp="3jbfpysf0kb4x">
      <Eyebrow data-id="element-57" data-mp="3j9mixc0hnowe">
        <span className="inline-flex items-center gap-2" data-id="element-58" data-mp="3j92z1pra2qfq">
          <TrophyIcon className="h-4 w-4" data-id="element-59" data-mp="3ebkzur32bpkx" /> El sistema Espalda y Columna 2026
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-60" data-mp="fn482uz8go5o">
        5 tomos. <span className="text-accent" data-id="element-61" data-mp="fo70x8g490o8">Toda tu columna cubierta.</span>
      </SectionTitle>
      <p className="mt-4 text-center font-medium text-slate-600" data-id="element-62" data-mp="atja32xn3u6h">
        De la movilidad básica al entrenamiento completo. Tocá un tomo para ver el contenido.
      </p>

      <div className="mt-8 space-y-3" data-id="element-63" data-mp="2a19ho4v71dkp">
        {tomos.map((tomo, index) => <div key={tomo.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors ${open === index ? 'border-primary shadow-lg shadow-sky-100' : 'border-sky-100'}`} data-id="element-64" data-mp="1veg9rko07a2j" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-7 md:py-5" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-65" data-mp="1mbf4tnavjp0g">
              <span className="flex items-center gap-3" data-id="element-66" data-mp="1hdnc4r2enyv7">
                <span className="text-2xl" data-id="element-67" data-mp="1he5iigis7tgu">{tomo.icon}</span>
                <span data-id="element-68" data-mp="36sk0mhzaoi3d">
                  <b className="block text-slate-900" data-id="element-69" data-mp="36td336r80lfp">
                    Tomo {index + 1} — {tomo.name}
                  </b>
                  <small className="font-bold text-accent" data-id="element-70" data-mp="3bpkaz536g70a">{tomo.period}</small>
                </span>
              </span>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-71" data-mp="v5qdtaek6op6" />
            </button>
            <AnimatePresence initial={false} data-id="element-72" data-mp="153o03scuybf5">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} data-id="element-73" data-mp="151l0cglwl3x8">
                  <ul className="space-y-2 border-t border-sky-100 px-5 py-4 text-sm font-medium text-slate-600 md:px-7" data-id="element-74" data-mp="1dyc7bo1zu36l">
                    {tomo.content.map(line => <li key={line} className="flex gap-2" data-id="element-75" data-mp="1nux8fyaw6u55">
                        <span className="text-accent" data-id="element-76" data-mp="3ujtt5rp7hmv9">→</span>
                        {line}
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>
    </section>;
}
export function Bonuses() {
  return <section className="border-t border-sky-100 bg-white px-4 py-12 md:py-16" data-id="element-77" data-mp="38pv1mf3sd3d5">
      <div className="mx-auto max-w-6xl" data-id="element-78" data-mp="3dj7jm6dymbwx">
        <Eyebrow data-id="element-79" data-mp="3dkijvw8751ja">
          <span className="inline-flex items-center gap-2" data-id="element-80" data-mp="3igzkhpwl18d9">
            <GiftIcon className="h-4 w-4" data-id="element-81" data-mp="3igfz1z7q41uh" /> Bonos exclusivos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-82" data-mp="2kvbm4zgkgfz3">
          Además te llevás <span className="text-accent" data-id="element-83" data-mp="2psbrt99i7gpo">5 bonos incluidos gratis</span>
        </SectionTitle>
        <div className="mt-9 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-84" data-mp="2urof1v4aepss">
          {bonuses.map(([number, icon, name, description, value]) => <article key={number} className="flex flex-col rounded-2xl border border-sky-100 bg-white p-5 shadow-sm" data-id="element-85" data-mp="2yu1keif4glp1">
              <div className="flex gap-3" data-id="element-86" data-mp="e2rc6j3ubn02">
                <span className="text-3xl" data-id="element-87" data-mp="e1q2ahyk96jx">{icon}</span>
                <div data-id="element-88" data-mp="1yktr187a3g26">
                  <small className="font-bold uppercase tracking-wide text-accent" data-id="element-89" data-mp="1yk0h5yd0waft">
                    Bono {number}
                  </small>
                  <h3 className="font-bold text-slate-900" data-id="element-90" data-mp="1ulkxo4g5hk75">{name}</h3>
                </div>
              </div>
              <p className="my-4 flex-1 text-sm font-medium leading-relaxed text-slate-600" data-id="element-91" data-mp="1pqf36h8omx7g">
                {description}
              </p>
              <div className="mt-auto flex border-t border-sky-100 pt-4 text-sm font-bold" data-id="element-92" data-mp="1fuu3wqv00b1e">
                <span className="text-slate-400 line-through" data-id="element-93" data-mp="1av7t0o2n76vr">Valor: {value}</span>
                <span className="ml-auto text-accent" data-id="element-94" data-mp="16tm6sc47xg8w">GRATIS</span>
              </div>
            </article>)}
        </div>
        <p className="mt-8 text-center font-medium text-slate-600" data-id="element-95" data-mp="11vufnkbedy5r">
          Valor total de bonos: <s className="font-bold text-slate-400" data-id="element-96" data-mp="316vm4kjg3vz2">$64.000 ARS</s> — hoy{' '}
          <strong className="text-accent" data-id="element-97" data-mp="2w93mf4ck75kb">incluidos gratis</strong> con tu compra.
        </p>
      </div>
    </section>;
}
export function FAQ() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-16" data-id="element-98" data-mp="206bkchc73fco">
      <Eyebrow data-id="element-99" data-mp="2085y9bp1u779">
        <span className="inline-flex items-center gap-2" data-id="element-100" data-mp="2e5uhyg7dr8aq">
          <HelpCircleIcon className="h-4 w-4" data-id="element-101" data-mp="2e5ayuw5zudv4" /> Preguntas frecuentes
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-102" data-mp="2979gyekb0kl8">
        Lo que más <span className="text-accent" data-id="element-103" data-mp="294x3adlkwwdz">nos preguntan</span>
      </SectionTitle>
      <div className="mt-9 space-y-3" data-id="element-104" data-mp="1lhf6g3ngj845">
        {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white ${open === index ? 'border-primary shadow-lg shadow-sky-100' : 'border-sky-100'}`} data-id="element-105" data-mp="1hh51mubeinqv" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-3 p-5 text-left font-bold text-slate-900 md:px-6" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-106" data-mp="1qexii2wru3gd">
              {question}
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-107" data-mp="39ddbl6xgbmot" />
            </button>
            <AnimatePresence initial={false} data-id="element-108" data-mp="30fjdbqg4miz0">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="border-t border-sky-100 px-5 py-4 text-sm font-medium leading-relaxed text-slate-600 md:px-6" data-id="element-109" data-mp="35cjhtwxbvdo0">
                  {answer}
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>
    </section>;
}
export function Guarantee() {
  return <section className="border-y border-sky-100 bg-sky-50/70 px-4 py-12 md:py-16" data-id="element-110" data-mp="27nzsy37smyvf">
      <div className="mx-auto max-w-3xl text-center" data-id="element-111" data-mp="2cpgnlgzopp3k">
        <span className="inline-flex rounded-full border-2 border-primary bg-sky-100 p-5 shadow-lg shadow-sky-100" data-id="element-112" data-mp="1smb33wjbkk1g">
          <ShieldIcon className="h-10 w-10 text-accent" data-id="element-113" data-mp="1okfu05v5d2d4" />
        </span>
        <Eyebrow data-id="element-114" data-mp="1ju9504wq3fuc">🛡️ Sin riesgo · 100% garantía</Eyebrow>
        <SectionTitle data-id="element-115" data-mp="1jsxz9zjpvy0l">
          Probalo 7 días. Si no te sirve,{' '}
          <span className="text-accent" data-id="element-116" data-mp="1evxv5u6fh5c4">te devolvemos el 100%.</span>
        </SectionTitle>
        <div className="mt-7 rounded-2xl border border-sky-100 bg-white p-6 text-left font-medium leading-relaxed text-slate-600 md:p-8" data-id="element-117" data-mp="19wkxsfhxkfva">
          <p data-id="element-118" data-mp="14vf4afgg4okp">
            Probalo una semana completa.{' '}
            <b className="text-slate-900" data-id="element-119" data-mp="10uv7xe3p5ozm">Aplicás las rutinas en casa o en el gimnasio.</b> Y si
            sentís que no era lo que esperabas, escribís a compradigitalyaa@gmail.com y te
            devolvemos el dinero.
          </p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm font-bold text-slate-900" data-id="element-120" data-mp="2v8w4fsbrxzwj">
            {['Sin vueltas', 'Sin formularios', 'Sin preguntas'].map(text => <span key={text} className="flex items-center gap-1.5" data-id="element-121" data-mp="2xum1wsb21xwt">
                <CheckCircle2Icon className="h-5 w-5 text-accent" data-id="element-122" data-mp="32sx9ewdvnwbv" />
                {text}
              </span>)}
          </div>
        </div>
        <p className="mt-6 text-lg font-bold text-slate-900" data-id="element-123" data-mp="37kym2jeyjp2f">
          El riesgo es nuestro, <span className="text-accent" data-id="element-124" data-mp="3chozeyzdo6k5">no tuyo.</span>
        </p>
        <p className="mt-6 text-base font-bold text-slate-700 md:text-xl" data-id="element-125" data-mp="2ev823tqn9sye">
          La próxima semana puede ser igual.{' '}
          <span className="text-accent" data-id="element-126" data-mp="2jt9nwmfgg6dk">O puede ser la primera con un sistema real.</span>
        </p>
        <div className="mt-7" data-id="element-127" data-mp="2os2zuaf3fpbm">
          <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-128" data-mp="2orul0czeu9xs">
            EMPEZAR HOY CON EL SISTEMA COMPLETO →
          </Button>
        </div>
        <p className="mt-5 inline-flex items-center gap-2 text-xs font-medium text-slate-500" data-id="element-129" data-mp="41vhcfpeen0z">
          <CheckIcon className="h-4 w-4 text-accent" data-id="element-130" data-mp="83qq259ns2os" />
          Pago 100% seguro · Acceso en 5 minutos · Mercado Pago
        </p>
      </div>
    </section>;
}